import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { BiPlus } from 'react-icons/bi'
import { HiOutlineUserGroup } from 'react-icons/hi'
import * as authAction from '../store/authAction'
import { useAuthGate } from '../comps/authPrompt'
import { createCommunity, fetchCommunities } from '../data/communities_data'
import CommunityJoinRules from '../comps/communityJoinRules'
import { uploadPostFile } from '../utils/postImages'
import UserAvatar from '../comps/userAvatar'
import '../styles/community.css'

const Communities = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { requireAuth, promptEl } = useAuthGate()
  // Show the last list straight away while the fresh one loads, so the page is never blank.
  const [list, setList] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('pinweelo-communities') || '[]') } catch (err) { return [] }
  })
  const [loaded, setLoaded] = useState(false)
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [bio, setBio] = useState('')
  const [file, setFile] = useState(null)
  const [joinRule, setJoinRule] = useState('admin')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const userData = localStorage.getItem('userdata')
    if (!userData) return
    try {
      const { id, pid } = JSON.parse(userData)
      dispatch(authAction.auth(id, pid))
    } catch (err) {}
  }, [dispatch])

  useEffect(() => {
    let alive = true
    const attempt = (left) => {
      fetchCommunities().then((rows) => {
        if (!alive) return
        setList(rows)
        setError('')
        setLoaded(true)
        try { sessionStorage.setItem('pinweelo-communities', JSON.stringify(rows)) } catch (err) {}
      }).catch((err) => {
        if (!alive) return
        if (left > 0) { setTimeout(() => attempt(left - 1), 1500); return }
        setError(err.message || 'Could not load communities')
        setLoaded(true)
      })
    }
    attempt(2)
    return () => { alive = false }
  }, [])

  const onCreate = async (event) => {
    event.preventDefault()
    if (!requireAuth()) return
    setSaving(true)
    setError('')
    try {
      let img = ''
      if (file) img = await uploadPostFile(file, 0)
      const community = await createCommunity({ name, bio, img, joinRule })
      navigate(`/community/${community.id}`)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="CommunityPage">
      {promptEl}
      <Helmet>
        <title>Community</title>
        <meta name="theme-color" content="#000000" />
      </Helmet>
      <header className="communityHead">
        <div>
          <p className="communityEyebrow">People</p>
          <h1>Community</h1>
        </div>
        <button type="button" className="communityCreateBtn" onClick={() => { if (requireAuth()) setOpen(true) }}>
          <BiPlus /> Create community
        </button>
      </header>
      {error && <p className="communityError">{error}</p>}
      <div className="communityList">
        {list.length === 0 && !loaded && (
          <div className="communityEmpty">
            <HiOutlineUserGroup />
            <p>Loading communities…</p>
          </div>
        )}
        {list.length === 0 && loaded && !error && (
          <div className="communityEmpty">
            <HiOutlineUserGroup />
            <p>No communities yet. Create one from here or from Rooms.</p>
          </div>
        )}
        {list.map((item) => (
          <button type="button" key={item.id} className="communityCard" onClick={() => navigate(`/community/${item.id}`)}>
            <UserAvatar src={item.img} name={item.name} />
            <span>
              <strong>{item.name}</strong>
              <em>{item.memberCount} {item.memberCount === 1 ? 'member' : 'members'}</em>
            </span>
          </button>
        ))}
      </div>
      {open && (
        <div className="communityModal" onClick={() => setOpen(false)} role="presentation">
          <form className="communityModalCard" onClick={(event) => event.stopPropagation()} onSubmit={onCreate}>
            <h2>Create community</h2>
            <label>
              Name
              <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Night photographers" maxLength={40} required minLength={3} />
            </label>
            <label>
              Bio
              <textarea value={bio} onChange={(event) => setBio(event.target.value)} placeholder="What is this group about?" maxLength={400} rows={3} />
            </label>
            <label className="communityPhotoPick">
              Photo
              <input type="file" accept="image/*" onChange={(event) => setFile(event.target.files && event.target.files[0])} />
            </label>
            <CommunityJoinRules value={joinRule} onChange={setJoinRule} />
            <div className="communityModalActions">
              <button type="button" onClick={() => setOpen(false)}>Cancel</button>
              <button type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create community'}</button>
            </div>
          </form>
        </div>
      )}
    </section>
  )
}

export default Communities
